/**
 * Combat Stats Utilities
 * L4-EGE-002: calculateCreaturePerformance
 * L4-EGE-003: calculateMVP
 *
 * Utility functions for calculating post-battle creature performance
 * and MVP (Most Valuable Player) from combat data.
 */

import type { CombatCreature, CombatEvent, PlayerId, DamageDealtEventData } from '@drawn-of-war/shared';

/**
 * Performance metrics for a single creature after battle
 */
export interface CreaturePerformance {
  /** Unique unit ID from combat */
  unitId: string;
  /** Original creature ID */
  creatureId: string;
  /** Display name of the creature */
  creatureName: string;
  /** Portrait/menu sprite URL for display */
  portraitUrl?: string;
  /** Player who owns this creature */
  ownerId: PlayerId;
  /** Total damage dealt to enemies */
  damageDealt: number;
  /** Total damage received from enemies */
  damageTaken: number;
  /** Whether creature survived the battle */
  survived: boolean;
  /** Ticks creature was alive (0 to battleDuration) */
  timeAlive: number;
  /** Total healing done to allies (including self) */
  healingDone: number;
}

/**
 * MVP scoring weights
 * These weights balance offensive contribution (damage), support (healing), and survival
 */
export const MVP_WEIGHTS = {
  /** Points per 1 damage dealt */
  damage: 1.0,
  /** Points per 1 healing done (weighted higher as healing is typically lower volume) */
  healing: 1.5,
  /** Bonus points for surviving the battle */
  survivalBonus: 100
} as const;

/**
 * Runtime structure for healing_done events
 * Note: This is not formally typed in shared types yet
 */
interface HealingDoneEventData {
  sourceUnitId?: string;
  targetUnitId: string;
  healing: number;
}

/**
 * Calculate performance metrics for all creatures from combat data
 *
 * @param units - All creatures that participated in combat (from finalState.units)
 * @param eventLog - Complete combat event log
 * @param battleDuration - Total battle duration in ticks
 * @returns Array of creature performance records
 */
export function calculateCreaturePerformance(
  units: CombatCreature[],
  eventLog: CombatEvent[],
  battleDuration: number
): CreaturePerformance[] {
  // Initialize performance records for each unit
  const performance = new Map<string, CreaturePerformance>();

  for (const unit of units) {
    performance.set(unit.unitId, {
      unitId: unit.unitId,
      creatureId: unit.creatureId,
      creatureName: unit.name || unit.creatureId,  // Fallback to ID if name missing
      portraitUrl: unit.portraitUrl,  // L4-EGE-PORTRAIT: Portrait for end-game roster
      ownerId: unit.ownerId,
      damageDealt: 0,
      damageTaken: 0,
      survived: unit.status === 'alive',
      timeAlive: unit.diedAtTick ?? battleDuration,
      healingDone: 0
    });
  }

  // Process event log
  for (const event of eventLog) {
    if (event.eventType === 'damage_dealt') {
      const data = event.data as DamageDealtEventData;

      // Attacker dealt damage
      const attacker = performance.get(data.attackerId);
      if (attacker) {
        attacker.damageDealt += data.damageAmount;
      }

      // Victim took damage
      const victim = performance.get(data.victimId);
      if (victim) {
        victim.damageTaken += data.damageAmount;
      }
    }

    if (event.eventType === 'healing_done') {
      // Runtime structure: { sourceUnitId?, targetUnitId, healing, ... }
      const data = event.data as HealingDoneEventData;
      if (data.sourceUnitId) {
        const healer = performance.get(data.sourceUnitId);
        if (healer) {
          healer.healingDone += data.healing;
        }
      }
    }
  }

  return Array.from(performance.values());
}

/**
 * Calculate MVP score for a single creature
 *
 * @param performance - Creature's performance metrics
 * @returns Numeric score for MVP comparison
 */
export function calculateMVPScore(performance: CreaturePerformance): number {
  const damageScore = performance.damageDealt * MVP_WEIGHTS.damage;
  const healingScore = performance.healingDone * MVP_WEIGHTS.healing;
  const survivalScore = performance.survived ? MVP_WEIGHTS.survivalBonus : 0;

  return damageScore + healingScore + survivalScore;
}

/**
 * Find the MVP creature for a specific team
 *
 * @param performances - All creature performances from the battle
 * @param teamOwnerId - The player ID to find MVP for
 * @returns Unit ID of the MVP creature, or undefined if team has no creatures
 *
 * Tie-breaker rules (in order):
 * 1. Higher damage dealt
 * 2. Higher healing done
 * 3. Longer time alive
 */
export function calculateMVP(
  performances: CreaturePerformance[],
  teamOwnerId: PlayerId
): string | undefined {
  const teamPerformances = performances.filter(p => p.ownerId === teamOwnerId);

  if (teamPerformances.length === 0) {
    return undefined;
  }

  // Sort by MVP score (descending), with tie-breakers
  const sorted = [...teamPerformances].sort((a, b) => {
    const scoreA = calculateMVPScore(a);
    const scoreB = calculateMVPScore(b);

    // Primary: MVP score
    if (scoreA !== scoreB) {
      return scoreB - scoreA;
    }

    // Tie-breaker 1: Damage dealt
    if (a.damageDealt !== b.damageDealt) {
      return b.damageDealt - a.damageDealt;
    }

    // Tie-breaker 2: Healing done
    if (a.healingDone !== b.healingDone) {
      return b.healingDone - a.healingDone;
    }

    // Tie-breaker 3: Time alive
    return b.timeAlive - a.timeAlive;
  });

  return sorted[0].unitId;
}
